import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Crown } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlan(data.plan);
          setState("paid");
          refreshUser();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (err) { console.warn("[PaymentSuccess] ignored error", err); }
      if (tries++ < 12) setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center px-4" style={{ background: "#F4F7FB" }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-2xl p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto" />
            <h1 className="text-xl font-bold text-slate-900 mt-4">Confirming your payment…</h1>
            <p className="text-slate-500 mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto glow-emerald">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-extrabold text-slate-900 mt-5 flex items-center justify-center gap-2"><Crown className="w-5 h-5 text-amber-600" /> Welcome to {plan || "Pro"}</h1>
            <p className="text-slate-500 mt-2">Your 7-day free trial is active. Time to get the reps in.</p>
            <Button onClick={() => navigate("/app")} className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold w-full h-11" data-testid="payment-continue-btn">Go to dashboard</Button>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto">
              <XCircle className="w-9 h-9 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mt-5">We couldn't confirm your payment</h1>
            <p className="text-slate-500 mt-2 text-sm">If you were charged, it'll activate shortly. Otherwise, try again.</p>
            <Button onClick={() => navigate("/app/pricing")} className="mt-6 bg-slate-100 hover:bg-slate-100 text-slate-900 border border-slate-200 w-full h-11" data-testid="payment-retry-btn">Back to pricing</Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
